import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLElement} [surface] – Interactive surface for a non-modal popover dialog.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#openingFrame) cancelAnimationFrame(this.#openingFrame);
    this.#openingFrame = 0;
    if (this.refs.dialog.hasAttribute('popover')) {
      this.closeDialog();
      document.removeEventListener('click', this.#handleClick, true);
      document.removeEventListener('keydown', this.#handleKeyDown, true);
    }
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #openingFrame = 0;
  /** @type {HTMLElement | null} */
  #opener = null;

  /**
   * Shows the dialog.
   */
  async showDialog() {
    const { dialog } = this.refs;
    const modeless = dialog.hasAttribute('popover');

    if (dialog.open || this.#openingFrame) return;
    if (modeless && document.querySelector('dialog:modal')) return;
    if (modeless && typeof dialog.showPopover !== 'function') {
      await import('@theme/popover-polyfill');
      if (!this.isConnected || dialog.open || this.#openingFrame) return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openingFrame = requestAnimationFrame(() => {
      this.#openingFrame = 0;
      if (modeless) {
        this.#opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      }
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      if (modeless) {
        // Keep the popup above page content without making the rest of the page inert.
        dialog.show();
        dialog.showPopover();
        document.addEventListener('click', this.#handleClick, true);
        document.addEventListener('keydown', this.#handleKeyDown, true);
      } else {
        dialog.showModal();
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      }
      this.dispatchEvent(new DialogOpenEvent());
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    const modeless = dialog.hasAttribute('popover');

    if (this.#openingFrame) cancelAnimationFrame(this.#openingFrame);
    this.#openingFrame = 0;

    if (!dialog.open) return;
    const restoreFocus = modeless && dialog.contains(document.activeElement);

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleClick, true);
    document.removeEventListener('keydown', this.#handleKeyDown, true);

    if (modeless) {
      // Complete dismissal during the outside click so its original target can act immediately.
      dialog.hidePopover();
    } else {
      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
    if (restoreFocus) {
      const opener = this.#opener?.isConnected
        ? this.#opener
        : [...document.querySelectorAll('[on\\:click]')].find(
            (trigger) =>
              trigger.getAttribute('on:click') === `#${this.id}/showDialog` && trigger.getClientRects().length,
          );
      if (opener instanceof HTMLElement) opener.focus({ preventScroll: true });
    }
    this.#opener = null;
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    const surface = this.refs.surface ?? dialog;
    if (isClickedOutside(event, surface)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true },
);
